import { OfficeArtifact, OfficeConversation, OfficeMessage, OfficeProgressStep, OfficeSource } from './types';
import { createClientId } from './clientId';
import { orderOfficeMessages } from './officeMessageOrder';

const STORAGE_PREFIX = 'ai_office_chats';
const MAX_MEMORY_CONVERSATIONS = 100;
export const OFFICE_CONVERSATIONS_UPDATED_EVENT = 'ai-office-conversations-updated';

const storageKey = (userId: string) => `${STORAGE_PREFIX}_${encodeURIComponent(userId)}`;
export const officeConversationStorageKey = storageKey;
export const officeConversationDeletionStorageKey = (userId: string, conversationId: string) =>
  `ai_office_deleted_chats_${encodeURIComponent(userId)}_${encodeURIComponent(conversationId)}`;

const mergeById = <T>(stored: T[] = [], incoming: T[] = [], id: (item: T) => string): T[] => {
  const merged = new Map(stored.map(item => [id(item), item]));
  incoming.forEach(item => merged.set(id(item), item));
  return [...merged.values()];
};

const isTerminal = (state?: string) => state === 'succeeded' || state === 'failed' || state === 'cancelled';

const eventSequence = (id?: string): [number, number] => {
  const match = /^(\d+)-(\d+)$/.exec(id || '');
  return match ? [Number(match[1]), Number(match[2])] : [0, 0];
};

const mergeMessage = (stored: OfficeMessage, incoming: OfficeMessage): OfficeMessage => {
  const storedTerminal = isTerminal(stored.jobState);
  const incomingTerminal = isTerminal(incoming.jobState);
  const storedEvent = eventSequence(stored.lastEventId);
  const incomingEvent = eventSequence(incoming.lastEventId);
  const storedAttempt = stored.attempt || 1;
  const incomingAttempt = incoming.attempt || 1;
  let newer: OfficeMessage;
  if (storedAttempt !== incomingAttempt) {
    newer = incomingAttempt > storedAttempt ? incoming : stored;
  } else if (storedTerminal !== incomingTerminal) {
    newer = incomingTerminal ? incoming : stored;
  } else {
    newer = incomingEvent[0] > storedEvent[0] || (incomingEvent[0] === storedEvent[0] && incomingEvent[1] >= storedEvent[1]) ? incoming : stored;
  }
  const other = newer === incoming ? stored : incoming;
  const sameAttempt = storedAttempt === incomingAttempt;
  return {
    ...other,
    ...newer,
    jobId: newer.jobId || other.jobId,
    jobState: sameAttempt && (!newer.jobState || newer.jobState === 'creating') && other.jobId ? other.jobState : newer.jobState,
    question: isTerminal(newer.jobState) ? undefined : newer.question,
    draft: isTerminal(newer.jobState) ? undefined : newer.draft,
    plan: newer.plan,
    content: !sameAttempt
      ? newer.content
      : isTerminal(newer.jobState) && newer.content
      ? newer.content
      : incoming.content.length > stored.content.length
      ? incoming.content
      : stored.content,
    artifacts: mergeById<OfficeArtifact>(stored.artifacts, incoming.artifacts, item => item.artifact_id),
    sources: sameAttempt ? mergeById<OfficeSource>(stored.sources, incoming.sources, item => item.source_id) : newer.sources || [],
    progress: sameAttempt ? mergeById<OfficeProgressStep>(other.progress, newer.progress, item => item.id) : newer.progress || [],
  };
};

export const mergeOfficeConversations = (stored: OfficeConversation, incoming: OfficeConversation): OfficeConversation => {
  const messages = new Map(stored.messages.map(message => [message.id, message]));
  incoming.messages.forEach(message => {
    const previous = messages.get(message.id);
    messages.set(message.id, previous ? mergeMessage(previous, message) : message);
  });
  const removedQueuedTurnIds = Array.from(new Set([...(stored.removedQueuedTurnIds || []), ...(incoming.removedQueuedTurnIds || [])]));
  const queuedTurns = new Map((stored.queuedTurns || []).map(turn => [turn.id, turn]));
  (incoming.queuedTurns || []).forEach(turn => {
    const previous = queuedTurns.get(turn.id);
    queuedTurns.set(turn.id, previous?.jobId && !turn.jobId ? previous : turn);
  });
  return {
    ...(incoming.updatedAt > stored.updatedAt ? incoming : stored),
    updatedAt: Math.max(stored.updatedAt, incoming.updatedAt),
    messages: orderOfficeMessages([...messages.values()]),
    queuedTurns: [...queuedTurns.values()].filter(turn => !messages.has(`office-user-${turn.id}`) && !removedQueuedTurnIds.includes(turn.id)),
    removedQueuedTurnIds,
  };
};

const isConversation = (value: unknown): value is OfficeConversation => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as OfficeConversation;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    Array.isArray(candidate.messages) &&
    typeof candidate.createdAt === 'number' &&
    typeof candidate.updatedAt === 'number'
  );
};

interface DeletedOfficeConversation {
  id: string;
  deleted: true;
}

const isDeletedConversation = (value: unknown): value is DeletedOfficeConversation =>
  Boolean(value && typeof value === 'object' && typeof (value as DeletedOfficeConversation).id === 'string' && (value as DeletedOfficeConversation).deleted === true);

const memoryEntries = new Map<string, Array<OfficeConversation | DeletedOfficeConversation>>();
let storageEpoch: Storage | undefined;

const resetForStorageEpoch = () => {
  const current = typeof localStorage === 'undefined' ? undefined : localStorage;
  if (current !== storageEpoch) {
    storageEpoch = current;
    memoryEntries.clear();
  }
};

const readStoredEntries = (userId: string): Array<OfficeConversation | DeletedOfficeConversation> => {
  resetForStorageEpoch();
  const cached = memoryEntries.get(userId);
  if (cached) {
    return cached;
  }
  try {
    // One-time compatibility read. New conversation content is kept in memory
    // and persisted by the server history API, never written back here.
    const parsed = JSON.parse(localStorage.getItem(storageKey(userId)) || '[]');
    const entries = Array.isArray(parsed) ? parsed.filter(item => isConversation(item) || isDeletedConversation(item)) : [];
    memoryEntries.set(userId, entries);
    return entries;
  } catch {
    memoryEntries.set(userId, []);
    return [];
  }
};

const hasDeletionMarker = (userId: string, conversationId: string): boolean => readStoredEntries(userId).some(item => isDeletedConversation(item) && item.id === conversationId);

const writeMemoryEntries = (userId: string, entries: Array<OfficeConversation | DeletedOfficeConversation>) => {
  memoryEntries.set(userId, [...entries.filter(isConversation).slice(0, MAX_MEMORY_CONVERSATIONS), ...entries.filter(isDeletedConversation)]);
  window.dispatchEvent(new CustomEvent(OFFICE_CONVERSATIONS_UPDATED_EVENT, { detail: { userId } }));
};

export const listOfficeConversations = (userId: string): OfficeConversation[] => {
  if (!userId || typeof window === 'undefined') {
    return [];
  }
  return readStoredEntries(userId)
    .filter(isConversation)
    .filter(item => !hasDeletionMarker(userId, item.id))
    .sort((left, right) => right.updatedAt - left.updatedAt);
};

export const getOfficeConversation = (userId: string, conversationId: string): OfficeConversation | null =>
  listOfficeConversations(userId).find(conversation => conversation.id === conversationId) || null;

export const isOfficeConversationDeleted = (userId: string, conversationId: string): boolean =>
  Boolean(
    userId &&
      conversationId &&
      typeof window !== 'undefined' &&
      (hasDeletionMarker(userId, conversationId) || readStoredEntries(userId).some(item => isDeletedConversation(item) && item.id === conversationId)),
  );

export const saveOfficeConversation = (userId: string, conversation: OfficeConversation): boolean => {
  if (!userId || typeof window === 'undefined') {
    return false;
  }
  const entries = readStoredEntries(userId);
  if (isOfficeConversationDeleted(userId, conversation.id)) {
    return false;
  }
  const existing = entries.filter(isConversation).filter(item => !hasDeletionMarker(userId, item.id));
  const stored = existing.find(item => item.id === conversation.id);
  const conversations = existing.filter(item => item.id !== conversation.id);
  conversations.unshift(stored ? mergeOfficeConversations(stored, conversation) : conversation);
  writeMemoryEntries(userId, [...conversations, ...entries.filter(isDeletedConversation)]);
  return true;
};

export const deleteOfficeConversation = (userId: string, conversationId: string): boolean => {
  if (!userId || typeof window === 'undefined') {
    return false;
  }
  const entries = readStoredEntries(userId);
  const conversations = entries.filter(isConversation).filter(item => item.id !== conversationId && !hasDeletionMarker(userId, item.id));
  const tombstones = entries.filter(isDeletedConversation).filter(item => item.id !== conversationId);
  writeMemoryEntries(userId, [...conversations, ...tombstones, { id: conversationId, deleted: true }]);
  return true;
};

export const replaceOfficeConversationIndex = (userId: string, remote: OfficeConversation[]): void => {
  const existing = readStoredEntries(userId);
  const current = new Map(existing.filter(isConversation).map(item => [item.id, item]));
  const merged = remote.map(item => {
    const local = current.get(item.id);
    return local ? mergeOfficeConversations(local, item) : item;
  });
  const remoteIds = new Set(remote.map(item => item.id));
  const pending = [...current.values()].filter(item => !remoteIds.has(item.id) && (item.queuedTurns?.length || item.messages.some(message => message.jobState === 'creating')));
  writeMemoryEntries(userId, [...merged, ...pending]);
};

export const clearLegacyOfficeHistoryIfCovered = (userId: string, messageCounts: Map<string, number>): boolean => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(userId)) || '[]');
    const entries = Array.isArray(parsed) ? parsed.filter(item => isConversation(item) || isDeletedConversation(item)) : [];
    const conversations = entries.filter(isConversation);
    if (conversations.some(item => !messageCounts.has(item.id) || (messageCounts.get(item.id) || 0) < item.messages.length)) {
      return false;
    }
    localStorage.removeItem(storageKey(userId));
    entries.forEach(item => localStorage.removeItem(officeConversationDeletionStorageKey(userId, item.id)));
    return true;
  } catch {
    return false;
  }
};

export const createOfficeConversation = (firstMessage: string): OfficeConversation => {
  const now = Date.now();
  return {
    id: `office-${createClientId()}`,
    title: firstMessage.slice(0, 100),
    messages: [],
    createdAt: now,
    updatedAt: now,
  };
};
